import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  DOWNLOAD_TRAVEL_REPORT,
  DOWNLOAD_TRAVEL_REPORT_HR,
  SUPERVISOR_BDM_TRAVEL_REPORT,
} from "../../constants";
import {
  Card,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  TableFooter,
  Paper,
  TablePagination,
  Grid,
  Box,
  Divider,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import FilterListIcon from "@mui/icons-material/FilterList";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import { Download, Map } from "lucide-react";
import * as FileSaver from "file-saver";

const buttonStyle = {
  border: "1px solid #fff",
  borderRadius: "4px",
  margin: "5px",
  width: "40px",
  height: "40px",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#097969",
  color: "#fff",
  transition: "background-color 0.3s, transform 0.2s",
  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
};

const hoverEffects = (e, isHovering) => {
  e.currentTarget.style.backgroundColor = isHovering ? "#0B9B73" : "#097969";
  e.currentTarget.style.transform = isHovering ? "scale(1.05)" : "scale(1)";
};

const SupervisorAttendanceReportComponent = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    localStorage.getItem("selectedDate") ||
      dayjs().subtract(1, "day").format("YYYY-MM-DD")
  );
 const [endDate, setEndDate] = useState(
  localStorage.getItem("endDate") ||
      dayjs().subtract(1, "day").format("YYYY-MM-DD")
    );
  const [filterShow, setFilterShow] = useState(false);

  const navigate = useNavigate();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("");
  const [message, setMessage] = useState("");

  const fetchAttendanceData = async (date, eDate) => {
    try {
      setLoading(true);
      const formattedStartDate = dayjs(date).format("YYYY-MM-DD");
      const formattedEndDate = dayjs(eDate).format("YYYY-MM-DD");

      const response = await axios.get(
        `${SUPERVISOR_BDM_TRAVEL_REPORT}?startDate=${formattedStartDate}&endDate=${formattedStartDate}`
      );

      if (response.data?.data) {
        setAttendanceData(response.data.data);
        setSummary(response.data.summary || {});
      } else {
        setAttendanceData([]);
        setSummary({});
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceData(selectedDate, endDate);
  }, []);

  const handleApply = () => {
    fetchAttendanceData(selectedDate, endDate); // Fetch data for the selected date
    setFilterShow(false);
    localStorage.setItem("selectedDate", selectedDate); // Save selected date to localStorage
    localStorage.setItem("endDate", endDate);
  };

  const handleClear = () => {
    const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
    setSelectedDate(yesterday); // Reset to yesterday
    setEndDate(yesterday);
    fetchAttendanceData(yesterday, yesterday);
    localStorage.removeItem("selectedDate");
    localStorage.removeItem("endDate");
    setFilterShow(false);
    setAttendanceData([]);
    setSummary({});
  };

  const handleIconClick = (employee) => {
    navigate("/attendanceReport/details", {
      state: {
        prevFilters: selectedDate,
        edate: selectedDate,
        bdmId: employee?.employeeDetails?.employeeId,
      },
    });
  };

  const handleMapClick = (employee) => {
    navigate("/attendanceReport/details/map", {
      state: {
        prevFilters: selectedDate,
        bdmId: employee?.employeeDetails?.employeeId,
        bdmName: employee?.employeeDetails?.employeeName,
      },
    });
  };

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const handleDownloadClick = async (employee) => {
    try {
      setDownloadLoading(true);
      setDownloadProgress(0);
      const startDate = dayjs(selectedDate).format("YYYY-MM-DD");
      const eDate = dayjs(endDate).format("YYYY-MM-DD");
 
      const response = await axios.get(
        `${DOWNLOAD_TRAVEL_REPORT_HR}?bdmId=${(employee?.employeeDetails?.employeeId)}&startDate=${startDate}&endDate=${eDate}`,
        {
          responseType: "blob", // Receive the file as a blob
          onDownloadProgress: (progressEvent) => {
            const percentage = Math.floor(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setDownloadProgress(percentage);
          },
        }
      );

      if (response.data) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        let fileName = "travel_report.xlsx";
        const contentDisposition = response.headers["content-disposition"];
        if (contentDisposition && contentDisposition.includes("filename=")) {
          fileName = contentDisposition
            .split("filename=")[1]
            .replace(/"/g, "")
            .trim();
        }

        // Trigger file download
        FileSaver.saveAs(blob, fileName);

        // Show success message
        setOpenSnackbar(true);
        setMessage("Report downloaded successfully.");
        setSeverity("success");
      } else {
        setOpenSnackbar(true);
        setMessage("Failed to generate report.");
        setSeverity("error");
      }
    } catch (err) {
      console.error("Error downloading file:", err);

      setOpenSnackbar(true);
      setMessage(
        err.response?.data?.message ||
          "An error occurred while downloading the report."
      );
      setSeverity("error");
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  // Conditional Rendering
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh", // Full viewport height
          backgroundColor: "#f9f9f9",
        }}
      >
        <Card
          sx={{
            padding: 4,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            boxShadow: 3,
            maxWidth: 400,
            width: "90%", // Responsive width
          }}
        >
          <CircularProgress size={60} sx={{ color: "#097969" }} />
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
            Fetching data...
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please wait while we prepare the report for you.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          marginBottom: 1,
        }}
      >
        <div>
          <Typography variant="h4" sx={{ fontWeight: "bold", marginBottom: 2 }}>
            Attendance Report
          </Typography>
        </div>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1, // Space between items
          }}
        >
          {filterShow && (
            <Box
              sx={{
                backgroundColor: "#f0f4f8",
                padding: 1,
                borderRadius: 2,
                boxShadow: 3,
                width: "100%",
              }}
            >
              <Grid container spacing={2}>
                {/* Date Pickers */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Start Date"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                </Grid>

                {/* End Date Picker */}
                {/* <Grid item xs={12} sm={4}>
                  <TextField
                    label="End Date"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Grid> */}

                {/* Action Buttons */}
                <Grid item xs={12} sm={3}>
                  <Box sx={{ display: "flex" }}>
                    <Button
                      onClick={handleClear}
                      variant="outlined"
                      color="secondary"
                      sx={{ marginRight: 2 }}
                    >
                      Clear
                    </Button>
                    <Button
                      onClick={handleApply}
                      variant="contained"
                      color="primary"
                    >
                      Apply
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          <div>
            {/* Filter Icon */}
            <IconButton
              onClick={() => setFilterShow((prev) => !prev)}
              style={buttonStyle}
              onMouseEnter={(e) => hoverEffects(e, true)}
              onMouseLeave={(e) => hoverEffects(e, false)}
            >
              <FilterListIcon />
            </IconButton>
          </div>
        </Box>
      </Box>

      {/* Summary */}
      <Typography variant="subtitle1" sx={{ marginBottom: 2, mt: 2 }}>
        <strong>Attendance Period:</strong> &nbsp;
        {summary?.dateRange?.from || "-"} to {summary?.dateRange?.to || "-"}
      </Typography>

      <Divider sx={{ marginBottom: 2 }} />

      {/* Employee Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table
          sx={{
            minWidth: 650,
            "& .MuiTableCell-root": {
              border: "1px solid rgba(224, 224, 224, 1)",
            },
          }}
          aria-label="attendance table"
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: "#EEF0F4" }}>
              {[
                "Employee Name",
                "Employee Phone",
                "Attendance In",
                "Attendance Out",
                // "Status",
                "Total Duration",
                "Action",
              ].map((header, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "8px",
                    fontSize: "0.9rem",
                    color: "#333",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {attendanceData.length > 0 ? (
              attendanceData.map((employee, index) => {
                const latestRecord = employee.attendanceRecords?.[0] || {};
                return (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F9FAFB",
                      "&:hover": { backgroundColor: "#F1F5F9" },
                    }}
                  >
                    <TableCell align="center">
                      {employee.employeeDetails?.employeeName || "-"}
                    </TableCell>
                    <TableCell align="center">
                      {employee.employeeDetails?.employeePhone || "-"}
                    </TableCell>
                    <TableCell align="center">
                      {latestRecord.inDetails?.time || "-"}
                    </TableCell>
                    <TableCell align="center">
                      {latestRecord.outDetails?.time || "-"}
                    </TableCell>
                    {/* <TableCell align="center">
                      {latestRecord.status || "-"}
                    </TableCell> */}
                    <TableCell align="center">
                      {latestRecord.workingDuration || "-"}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex", // Flex layout for icon buttons
                          gap: "4px", // Add gap between buttons
                          justifyContent: "flex-start", // Center align buttons horizontally
                          alignItems: "flex-start", // Center align buttons vertically
                          padding: "4px", // Optional padding around the group
                        }}
                      >
                        <Tooltip title="View" arrow>
                          <IconButton
                            style={{
                              border: "1px solid",
                              borderRadius: "4px",
                              padding: "2px",
                              width: "24px",
                              height: "24px",
                              justifyContent: "center",
                              alignItems: "center",
                              backgroundColor: "#fff",
                            }}
                            color="primary"
                            onClick={() => handleIconClick(employee)}
                          >
                            <VisibilityIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="View Map" arrow>
                          <IconButton
                            style={{
                              border: "1px solid",
                              borderRadius: "4px",
                              padding: "2px",
                              width: "24px",
                              height: "24px",
                              justifyContent: "center",
                              alignItems: "center",
                              backgroundColor: "#fff",
                            }}
                            color="primary"
                            onClick={() => handleMapClick(employee)}
                          >
                            <Map />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download Report" arrow>
                          <IconButton
                            style={{
                              border: "1px solid",
                              borderRadius: "4px",
                              padding: "2px",
                              width: "24px",
                              height: "24px",
                              justifyContent: "center",
                              alignItems: "center",
                              backgroundColor: "#fff",
                            }}
                            color="primary"
                            onClick={() => handleDownloadClick(employee)}
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell align="center" colSpan={6}>
                  No attendance data available for the selected date.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000} // Snackbar will auto hide after 6 seconds
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={severity}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>

      {/* Backdrop with Circular Loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={downloadLoading}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Downloading Report...
          </Typography>
        </Box>
      </Backdrop>
    </div>
  );
};

export default SupervisorAttendanceReportComponent;
